import { supabase } from '@/lib/supabase/client';
import type {
  ModelFile,
  ModelFileInsert,
  ModelFileType,
  VersionFile,
  VersionFileInsert,
} from '@/lib/supabase/types';

// Maximum size for text content stored directly in DB (500KB)
const MAX_TEXT_CONTENT_SIZE = 500 * 1024;

// File types that should be stored as binary in storage bucket
const BINARY_FILE_TYPES: ModelFileType[] = ['stl'];

// ============================================
// MODEL FILES CRUD
// ============================================

/**
 * Get all files for a model
 */
export async function getModelFiles(modelId: string): Promise<ModelFile[]> {
  const { data, error } = await supabase
    .from('model_files')
    .select('*')
    .eq('model_id', modelId)
    .order('filename');

  if (error) {
    console.error('Error fetching model files:', error);
    throw new Error(error.message);
  }

  return data || [];
}

/**
 * Get a single file by ID
 */
export async function getModelFile(fileId: string): Promise<ModelFile | null> {
  const { data, error } = await supabase
    .from('model_files')
    .select('*')
    .eq('id', fileId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    console.error('Error fetching model file:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Create or update a file for a model
 * Handles both text content (stored in DB) and binary files (stored in bucket)
 */
export async function upsertModelFile(
  modelId: string,
  filename: string,
  content: string | ArrayBuffer,
  fileType: ModelFileType,
): Promise<ModelFile> {
  const isBinary = BINARY_FILE_TYPES.includes(fileType);
  const size =
    typeof content === 'string' ? content.length : content.byteLength;

  // Check if file already exists
  const { data: existing } = await supabase
    .from('model_files')
    .select('id, storage_path')
    .eq('model_id', modelId)
    .eq('filename', filename)
    .single();

  let storagePath: string | null = null;
  let textContent: string | null = null;

  if (isBinary || size > MAX_TEXT_CONTENT_SIZE) {
    // Store in Supabase Storage bucket
    storagePath = `${modelId}/${filename}`;
    const blob =
      content instanceof ArrayBuffer
        ? new Blob([content])
        : new Blob([content], { type: 'text/plain' });

    const { error: uploadError } = await supabase.storage
      .from('model-files')
      .upload(storagePath, blob, {
        upsert: true,
        contentType: isBinary ? 'application/octet-stream' : 'text/plain',
      });

    if (uploadError) {
      console.error('Error uploading file to storage:', uploadError);
      throw new Error(uploadError.message);
    }
  } else {
    // Store text content directly in DB
    textContent =
      typeof content === 'string' ? content : new TextDecoder().decode(content);
  }

  const fileData: ModelFileInsert = {
    model_id: modelId,
    filename,
    file_type: fileType,
    file_size: size,
    content: textContent,
    storage_path: storagePath,
  };

  if (existing) {
    // Update existing file
    const { data, error } = await supabase
      .from('model_files')
      .update({
        file_size: size,
        content: textContent,
        storage_path: storagePath,
      })
      .eq('id', existing.id)
      .select()
      .single();

    if (error) {
      console.error('Error updating model file:', error);
      throw new Error(error.message);
    }

    // Clean up old storage file if path changed
    if (existing.storage_path && existing.storage_path !== storagePath) {
      await supabase.storage
        .from('model-files')
        .remove([existing.storage_path]);
    }

    return data;
  } else {
    // Insert new file
    const { data, error } = await supabase
      .from('model_files')
      .insert(fileData)
      .select()
      .single();

    if (error) {
      console.error('Error creating model file:', error);
      throw new Error(error.message);
    }

    return data;
  }
}

/**
 * Delete a file from a model
 */
export async function deleteModelFile(fileId: string): Promise<void> {
  // First get the file to check for storage path
  const file = await getModelFile(fileId);
  if (!file) return;

  // Delete from storage if applicable
  if (file.storage_path) {
    await supabase.storage.from('model-files').remove([file.storage_path]);
  }

  // Delete from database
  const { error } = await supabase
    .from('model_files')
    .delete()
    .eq('id', fileId);

  if (error) {
    console.error('Error deleting model file:', error);
    throw new Error(error.message);
  }
}

/**
 * Get the content of a file (handles both DB and storage)
 */
export async function getFileContent(
  file: ModelFile,
): Promise<string | ArrayBuffer> {
  if (file.content !== null) {
    // Content stored directly in DB
    return file.content;
  }

  if (file.storage_path) {
    // Download from storage bucket
    const { data, error } = await supabase.storage
      .from('model-files')
      .download(file.storage_path);

    if (error) {
      console.error('Error downloading file from storage:', error);
      throw new Error(error.message);
    }

    if (BINARY_FILE_TYPES.includes(file.file_type)) {
      return await data.arrayBuffer();
    } else {
      return await data.text();
    }
  }

  throw new Error('File has no content or storage path');
}

/**
 * Batch create files for a model (useful when forking)
 */
export async function copyFilesToModel(
  sourceModelId: string,
  targetModelId: string,
): Promise<ModelFile[]> {
  const sourceFiles = await getModelFiles(sourceModelId);
  const copiedFiles: ModelFile[] = [];

  for (const file of sourceFiles) {
    const content = await getFileContent(file);
    const copied = await upsertModelFile(
      targetModelId,
      file.filename,
      content,
      file.file_type,
    );
    copiedFiles.push(copied);
  }

  return copiedFiles;
}

// ============================================
// VERSION FILES
// ============================================

/**
 * Create version file snapshots from current model files
 */
export async function createVersionFiles(
  versionId: string,
  modelId: string,
): Promise<void> {
  const modelFiles = await getModelFiles(modelId);

  for (const file of modelFiles) {
    const content =
      file.content || (file.storage_path ? file.storage_path : null);

    const versionFile: VersionFileInsert = {
      version_id: versionId,
      filename: file.filename,
      file_type: file.file_type,
      file_size: file.file_size,
      content: typeof content === 'string' ? content : null,
      storage_path: file.storage_path,
    };

    const { error } = await supabase.from('version_files').insert(versionFile);

    if (error) {
      console.error('Error creating version file:', error);
      // Continue with other files even if one fails
    }
  }
}

/**
 * Get files for a specific version
 */
export async function getVersionFiles(
  versionId: string,
): Promise<VersionFile[]> {
  const { data, error } = await supabase
    .from('version_files')
    .select('*')
    .eq('version_id', versionId)
    .order('filename');

  if (error) {
    console.error('Error fetching version files:', error);
    throw new Error(error.message);
  }

  return data || [];
}

/**
 * Restore files from a version to a model
 */
export async function restoreVersionFiles(
  versionId: string,
  modelId: string,
): Promise<void> {
  const versionFiles = await getVersionFiles(versionId);

  // First, delete all current model files
  const currentFiles = await getModelFiles(modelId);
  for (const file of currentFiles) {
    await deleteModelFile(file.id);
  }

  // Then restore from version
  for (const vf of versionFiles) {
    let content: string | ArrayBuffer;

    if (vf.content !== null) {
      content = vf.content;
    } else if (vf.storage_path) {
      // Download from storage
      const { data, error } = await supabase.storage
        .from('model-files')
        .download(vf.storage_path);

      if (error) {
        console.error('Error downloading version file:', error);
        continue;
      }

      content = BINARY_FILE_TYPES.includes(vf.file_type)
        ? await data.arrayBuffer()
        : await data.text();
    } else {
      continue; // Skip files with no content
    }

    await upsertModelFile(modelId, vf.filename, content, vf.file_type);
  }
}
